import { Modal, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { IFood, MenuUpgrade } from "../models/IFood";
import { FoodCategory } from "../models/IFoodCategory";
import { getFoodCategories, updateFood, updateFoodPhoto, translateToEnglish } from "../services/food";

interface Props {
  food: IFood | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const emptyExtra: MenuUpgrade = {
  id: "",
  menuUpgradeTitle: "",
  menuUpgradePrice: 0,
  menuUpgradeMaxQuantity: 0,
  menuUpgradeIsEnabled: true,
};

function FoodUpdatePopup({ food, isOpen, onClose, onSaved }: Props) {
  const [formData, setFormData] = useState<IFood>(food ?? ({} as IFood));
  const [foodCategories, setFoodCategories] = useState<FoodCategory[]>([]);
  const [menuExtra, setMenuExtra] = useState<MenuUpgrade>(emptyExtra);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"genel" | "ekstra" | "resim">("genel");
  const [saving, setSaving] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [translating, setTranslating] = useState<{ nameEn?: boolean; descEn?: boolean }>({});

  const handleTranslate = async (field: "nameEn" | "descEn") => {
    const trText = field === "nameEn" ? formData.foodNameTr : formData.foodDescriptionTr;
    if (!trText?.trim()) return;
    setTranslating(prev => ({ ...prev, [field]: true }));
    try {
      const res = await translateToEnglish(trText.trim());
      if (field === "nameEn") set("foodNameEn", res.data);
      else set("foodDescriptionEn", res.data);
    } catch {}
    finally { setTranslating(prev => ({ ...prev, [field]: false })); }
  };

  useEffect(() => {
    if (food) {
      setFormData(food);
      setActiveTab("genel");
      setFile(null);
      setPreview(null);
    }
  }, [food]);

  useEffect(() => {
    getFoodCategories()
      .then(res => setFoodCategories(res.data ?? []))
      .catch(() => {});
  }, []);

  // 3 saniye geri sayım sonrası kapat
  useEffect(() => {
    if (countdown <= 0) return;
    if (countdown === 3) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setSaving(false);
            onSaved();
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [countdown]);

  const set = (field: keyof IFood, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  // Resim dışındaki alanlarda değişiklik var mı (resim yolu karşılaştırmaya girmez).
  const hasInfoChanges = (() => {
    if (!food) return false;
    const { foodImagePath: _a, ...current } = formData;
    const { foodImagePath: _b, ...original } = food;
    return JSON.stringify(current) !== JSON.stringify(original);
  })();

  // Tek kaydetme akışı: önce bilgiler (değiştiyse), en son resim (seçildiyse).
  // Resim en son yazıldığı için kazanır; ardından popup kapanır ki bayat formla
  // ikinci bir kayıt yapılamasın.
  const saveAll = async () => {
    if (saving) return;
    if (!hasInfoChanges && !file) {
      onClose();
      return;
    }
    setSaving(true);
    setPhotoUploading(!!file);
    try {
      if (hasInfoChanges) {
        try {
          await updateFood(formData);
        } catch {
          alert("Bilgiler güncellenemedi." + (file ? " Resim de yüklenmedi." : ""));
          setSaving(false);
          return;
        }
      }
      if (file && formData.id) {
        try {
          await updateFoodPhoto(formData.id, file);
        } catch {
          alert(hasInfoChanges
            ? "Bilgiler kaydedildi ama resim yüklenemedi. Resmi tekrar deneyin."
            : "Resim yüklenirken hata oluştu.");
          // Bilgiler kaydedildiyse liste güncellensin ama popup açık kalsın ki resim tekrar denensin.
          if (hasInfoChanges) onSaved();
          setSaving(false);
          return;
        }
      }
      setFile(null);
      setPreview(null);
      setCountdown(3); // başarı ekranı → 3 sn sonra onSaved + kapat
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleUpdate = () => { saveAll(); };

  const addExtra = () => {
    if (!menuExtra.menuUpgradeTitle.trim()) return;
    setFormData(prev => ({
      ...prev,
      menuUpgrade: [...prev.menuUpgrade, { ...menuExtra }],
    }));
    setMenuExtra(emptyExtra);
  };

  const removeExtra = (index: number) => {
    setFormData(prev => ({
      ...prev,
      menuUpgrade: prev.menuUpgrade.filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      setPreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handlePhotoUpload = () => {
    if (!formData.id || !file) {
      alert("Lütfen bir dosya seçin.");
      return;
    }
    saveAll();
  };

  const tabStyle = (tab: string): React.CSSProperties => ({
    padding: "8px 20px",
    border: "none",
    background: "none",
    borderBottom: activeTab === tab ? "3px solid #0d6efd" : "3px solid transparent",
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? "#0d6efd" : "#555",
    cursor: "pointer",
    transition: "all 0.2s",
  });

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered scrollable>
      {/* Başlık */}
      <Modal.Header closeButton style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          <i className="bi bi-pencil-square me-2 text-primary"></i>
          <strong>{formData.foodNameTr || "Yemek Düzenle"}</strong>
          {formData.foodIsEnabled ? (
            <span className="badge bg-success ms-2" style={{ fontSize: "0.7rem" }}>Aktif</span>
          ) : (
            <span className="badge bg-danger ms-2" style={{ fontSize: "0.7rem" }}>Pasif</span>
          )}
        </Modal.Title>
      </Modal.Header>

      {/* Kaydetme overlay */}
      {saving && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 9999,
          background: "rgba(255,255,255,0.92)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          borderRadius: "8px",
        }}>
          {countdown > 0 ? (
            <>
              <div style={{ fontSize: "3rem", color: "#198754" }}>
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div className="fw-bold mt-2" style={{ fontSize: "1.1rem", color: "#198754" }}>
                Güncelleme başarılı!
              </div>
              <div className="text-muted mt-1">{countdown} saniye içinde kapanıyor...</div>
              <div className="progress mt-3" style={{ width: "200px", height: "6px" }}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${(countdown / 3) * 100}%`, transition: "width 1s linear" }}
                />
              </div>
            </>
          ) : (
            <>
              <div className="spinner-border text-primary mb-2" role="status" />
              <div className="text-muted">Güncelleniyor...</div>
            </>
          )}
        </div>
      )}

      {/* Sekmeler */}
      <div style={{ display: "flex", borderBottom: "1px solid #e0e0e0", padding: "0 16px" }}>
        <button style={tabStyle("genel")} onClick={() => setActiveTab("genel")}>
          <i className="bi bi-info-circle me-1"></i>Genel Bilgiler
        </button>
        <button style={tabStyle("ekstra")} onClick={() => setActiveTab("ekstra")}>
          <i className="bi bi-plus-circle me-1"></i>Ekstralar
          {formData.menuUpgrade?.length > 0 && (
            <span className="badge bg-primary ms-1" style={{ fontSize: "0.7rem" }}>
              {formData.menuUpgrade.length}
            </span>
          )}
        </button>
        <button style={tabStyle("resim")} onClick={() => setActiveTab("resim")}>
          <i className="bi bi-image me-1"></i>Resim
        </button>
      </div>

      <Modal.Body style={{ padding: "20px 24px" }}>

        {/* ── GENEL BİLGİLER ── */}
        {activeTab === "genel" && (
          <div>
            <div className="row g-3">
              {/* Ad TR */}
              <div className="col-md-6">
                <label className="form-label fw-semibold small mb-1">Yemek Adı (TR)</label>
                <input
                  className="form-control"
                  value={formData.foodNameTr ?? ""}
                  maxLength={30}
                  onChange={e => set("foodNameTr", e.target.value)}
                />
              </div>
              {/* Ad EN */}
              <div className="col-md-6">
                <label className="form-label fw-semibold small mb-1 d-flex align-items-center justify-content-between">
                  Yemek Adı (EN)
                  {formData.foodNameTr?.trim() && (
                    <button type="button" className="translate-btn" onClick={() => handleTranslate("nameEn")} disabled={translating.nameEn}>
                      {translating.nameEn ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-translate"></i>}
                      Çevir
                    </button>
                  )}
                </label>
                <input
                  className="form-control"
                  value={formData.foodNameEn ?? ""}
                  maxLength={30}
                  onChange={e => set("foodNameEn", e.target.value)}
                />
              </div>
              {/* Açıklama TR */}
              <div className="col-md-6">
                <label className="form-label fw-semibold small mb-1">Açıklama (TR)</label>
                <input
                  className="form-control"
                  value={formData.foodDescriptionTr ?? ""}
                  maxLength={160}
                  onChange={e => set("foodDescriptionTr", e.target.value)}
                />
              </div>
              {/* Açıklama EN */}
              <div className="col-md-6">
                <label className="form-label fw-semibold small mb-1 d-flex align-items-center justify-content-between">
                  Açıklama (EN)
                  {formData.foodDescriptionTr?.trim() && (
                    <button type="button" className="translate-btn" onClick={() => handleTranslate("descEn")} disabled={translating.descEn}>
                      {translating.descEn ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-translate"></i>}
                      Çevir
                    </button>
                  )}
                </label>
                <input
                  className="form-control"
                  value={formData.foodDescriptionEn ?? ""}
                  maxLength={160}
                  onChange={e => set("foodDescriptionEn", e.target.value)}
                />
              </div>
              {/* İçerik */}
              <div className="col-md-12">
                <label className="form-label fw-semibold small mb-1">İçerik / Malzemeler</label>
                <input
                  className="form-control"
                  value={formData.foodContents ?? ""}
                  maxLength={160}
                  onChange={e => set("foodContents", e.target.value)}
                />
              </div>
              {/* Kategori */}
              <div className="col-md-6">
                <label className="form-label fw-semibold small mb-1">Kategori</label>
                <select
                  className="form-select"
                  value={formData.foodCategoryId ?? ""}
                  onChange={e => set("foodCategoryId", e.target.value)}
                >
                  <option value="" disabled>Kategori seçin</option>
                  {foodCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.foodCategoryNameTr}
                    </option>
                  ))}
                </select>
              </div>
              {/* Fiyat */}
              <div className="col-md-3">
                <label className="form-label fw-semibold small mb-1">Fiyat</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    value={formData.foodPrice ?? 0}
                    onChange={e => set("foodPrice", parseFloat(e.target.value) || 0)}
                  />
                  <span className="input-group-text">TL</span>
                </div>
              </div>
              {/* Aktif */}
              <div className="col-md-3 d-flex align-items-end pb-1">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="foodEnabled"
                    checked={formData.foodIsEnabled ?? false}
                    onChange={e => set("foodIsEnabled", e.target.checked)}
                    style={{ width: "2.5rem", height: "1.3rem" }}
                  />
                  <label className="form-check-label ms-1 fw-semibold" htmlFor="foodEnabled">
                    {formData.foodIsEnabled ? (
                      <span className="text-success">Aktif</span>
                    ) : (
                      <span className="text-danger">Pasif</span>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── EKSTRALAR ── */}
        {activeTab === "ekstra" && (
          <div>
            {/* Yeni ekstra formu */}
            <div className="p-3 rounded mb-3" style={{ background: "#f8f9fa", border: "1px solid #e0e0e0" }}>
              <div className="fw-semibold mb-2 small text-muted">
                <i className="bi bi-plus-lg me-1"></i>Yeni Ekstra Ekle
              </div>
              <div className="row g-2 align-items-end">
                <div className="col-md-5">
                  <label className="form-label small mb-1 text-muted fw-semibold">Başlık</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ör. Ketçap"
                    value={menuExtra.menuUpgradeTitle}
                    onChange={e => setMenuExtra(p => ({ ...p, menuUpgradeTitle: e.target.value }))}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1 text-muted fw-semibold">Fiyat</label>
                  <div className="input-group input-group-sm">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0"
                      value={menuExtra.menuUpgradePrice}
                      onChange={e => setMenuExtra(p => ({ ...p, menuUpgradePrice: parseFloat(e.target.value) || 0 }))}
                    />
                    <span className="input-group-text">TL</span>
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1 text-muted fw-semibold">Max Adet</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="0"
                    value={menuExtra.menuUpgradeMaxQuantity}
                    onChange={e => setMenuExtra(p => ({ ...p, menuUpgradeMaxQuantity: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-primary btn-sm w-100"
                    onClick={addExtra}
                    disabled={!menuExtra.menuUpgradeTitle.trim()}
                  >
                    <i className="bi bi-plus-lg"></i> Ekle
                  </button>
                </div>
              </div>
            </div>

            {/* Ekstralar listesi */}
            {formData.menuUpgrade?.length === 0 ? (
              <p className="text-muted text-center small py-3">Henüz ekstra eklenmemiş.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {formData.menuUpgrade?.map((ex, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
                    style={{ background: "#fff", border: "1px solid #e0e0e0" }}
                  >
                    <div>
                      <span className="fw-semibold">{ex.menuUpgradeTitle}</span>
                      <span className="ms-2 text-muted small">
                        {ex.menuUpgradePrice === 0 ? "Ücretsiz" : `${ex.menuUpgradePrice} TL`}
                        {" · "}Max {ex.menuUpgradeMaxQuantity} adet
                      </span>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      style={{ borderRadius: "50%", width: "28px", height: "28px", padding: 0, lineHeight: "26px" }}
                      onClick={() => removeExtra(i)}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── RESİM ── */}
        {activeTab === "resim" && (
          <div>
            {/* Mevcut resim */}
            {formData.foodImagePath && (
              <div className="text-center mb-4">
                <div className="small text-muted mb-2 fw-semibold">Mevcut Resim</div>
                <img
                  src={"https://mutfacik.s3.eu-central-1.amazonaws.com/" + formData.foodImagePath}
                  alt={formData.foodNameTr}
                  style={{ height: "180px", objectFit: "cover", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.15)" }}
                />
              </div>
            )}
            {/* Yeni resim yükleme */}
            <div className="p-3 rounded" style={{ background: "#f8f9fa", border: "1px dashed #ccc" }}>
              <label className="form-label fw-semibold small">Yeni Resim Seç</label>
              <input
                className="form-control mb-3"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              {preview && (
                <div className="text-center mb-3">
                  <div className="small text-muted mb-1">Önizleme:</div>
                  <img
                    src={preview}
                    alt="önizleme"
                    style={{ height: "160px", objectFit: "cover", borderRadius: "10px", border: "2px solid #0d6efd" }}
                  />
                </div>
              )}
              {file && hasInfoChanges && (
                <div className="small mb-2 p-2 rounded" style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Diğer sekmelerde yaptığın değişiklikler de resimle birlikte kaydedilecek.
                </div>
              )}
              <button
                className="btn btn-primary w-100"
                onClick={handlePhotoUpload}
                disabled={!file || saving}
              >
                {photoUploading ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" /><span>Kaydediliyor...</span></>
                ) : (
                  <><i className="bi bi-cloud-upload me-1"></i>{hasInfoChanges ? "Resmi ve Bilgileri Kaydet" : "Resmi Yükle"}</>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ background: "#f8f9fa", borderTop: "1px solid #e0e0e0" }}>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          <i className="bi bi-x-lg me-1"></i>Kapat
        </Button>
        {activeTab !== "resim" && (
          <Button variant="success" onClick={handleUpdate} disabled={saving}>
            {saving && countdown === 0 ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status" /><span>Güncelleniyor...</span></>
            ) : (
              <><i className="bi bi-check-lg me-1"></i>{file ? "Güncelle (resimle birlikte)" : "Güncelle"}</>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default FoodUpdatePopup;